import { style, globalStyle, keyframes } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion } from '../../theme/media';

// --- Keyframes ---

const backdropFadeIn = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

// --- FAB Button ---

export const fabButton = style({
  position: 'fixed',
  bottom: 'max(24px, env(safe-area-inset-bottom))',
  right: 'max(24px, env(safe-area-inset-right))',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  border: `1px solid ${vars.surface.border.secondary}`,
  background: vars.surface.background.glass,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  color: vars.bright.primary.light,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  zIndex: vars.elevation.floatingAction,
  boxShadow: `${vars.shadow.card}, 0 0 12px ${vars.bright.primary.glow}`,
  transition: `transform ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,

  selectors: {
    '&:focus-visible': {
      outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
      outlineOffset: vars.focus.ring.offset,
    },
  },

  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
});

export const fabButtonOpen = style({
  transform: 'scale(0.9)',
  boxShadow: `${vars.shadow.card}, 0 0 24px ${vars.bright.primary.glow}`,
});

// --- Backdrop ---

export const backdrop = style({
  position: 'fixed',
  inset: 0,
  background: vars.bright.overlay.darker,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.subtle,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.subtle,
  zIndex: `calc(${vars.elevation.floatingAction} - 1)`,
  animation: `${backdropFadeIn} ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,

  '@media': {
    [reducedMotion]: {
      animation: 'none',
    },
  },
});

// --- Quick navigation list ---

export const menu = style({
  position: 'fixed',
  bottom: 'calc(max(24px, env(safe-area-inset-bottom)) + 64px)',
  right: 'max(24px, env(safe-area-inset-right))',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '8px',
  borderRadius: '12px',
  background: vars.surface.background.primary,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  border: `1px solid ${vars.surface.highlight.strong}`,
  zIndex: vars.elevation.floatingAction,
  minWidth: '160px',

  // 항목 수는 권한에 따라 최대 13개까지 늘어난다. 상한이 없으면 메뉴가 뷰포트 위로
  // 자라 잘린 부분이 도달 불가가 된다(iPhone SE 급 667 높이에서 실제로 잘림).
  // `dvh` 는 iOS URL 바 수축을 흡수한다(선례: Modal.css.ts).
  maxHeight: 'calc(100dvh - max(24px, env(safe-area-inset-bottom)) - 64px - 16px)',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  scrollbarWidth: 'none',
});

// --- Sections (데스크톱 dock 과 같은 4구획: 내 작업/팀/전사 운영/시스템) ---

export const menuSection = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const sectionHeading = style({
  margin: 0,
  padding: '0 12px',
  color: vars.typography.textColor.tertiary,
  fontSize: vars.typography.fontSize.xs,
  fontWeight: String(vars.typography.fontWeight.bold),
  letterSpacing: vars.typography.letterSpacing.wide,
  lineHeight: String(1.2),
});

/**
 * 🔴 `display: flex` 를 주지 않는다.
 *
 * flex/grid 를 준 `ul` 은 Safari/VoiceOver 에서 list role 이 벗겨진다. 방어로 `role="list"` 를
 * 명시하면 이번엔 `jsx-a11y/no-redundant-roles` 가 막는다. 블록 `ul` 로 두면 양쪽이 다 해결된다 —
 * 항목 간격은 아래 `globalStyle` 의 인접 형제 마진으로 준다.
 */
export const sectionList = style({
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

// --- 캐퍼빌리티 대기·실패 표시 (항목이 걸러진 이유를 말하는 컴팩트 스트립) ---

export const capabilityNotice = style({
  margin: 0,
  padding: '8px 12px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
  background: vars.surface.highlight.medium,
  border: `1px solid ${vars.surface.border.secondary}`,
  color: vars.typography.textColor.secondary,
  fontSize: vars.typography.fontSize.xs,
  lineHeight: String(1.3),
});

export const capabilityNoticeText = style({
  margin: 0,
});

export const capabilityRetryButton = style({
  minHeight: '32px',
  padding: '4px 10px',
  borderRadius: '8px',
  border: `1px solid ${vars.surface.border.secondary}`,
  background: 'transparent',
  color: vars.typography.textColor.primary,
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  cursor: 'pointer',

  selectors: {
    '&:hover': { color: vars.bright.primary.light },
    '&:focus-visible': {
      outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
      outlineOffset: vars.focus.ring.offset,
    },
  },
});

export const menuItem = style({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '8px 12px',
  minHeight: '44px',
  textAlign: 'left',
  borderRadius: '8px',
  border: 'none',
  background: 'transparent',
  color: vars.typography.textColor.secondary,
  fontSize: vars.typography.fontSize.sm,
  cursor: 'pointer',
  fontFamily: vars.typography.fontFamily.primary,
  transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,

  selectors: {
    '&:hover, &:focus-visible': {
      background: vars.surface.highlight.medium,
      color: vars.bright.primary.light,
    },
  },

  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
});

export const menuItemIcon = style({
  display: 'inline-flex',
  flexShrink: 0,
});

export const menuItemLabel = style({
  whiteSpace: 'nowrap',
});

export const menuItemActive = style({
  background: vars.surface.highlight.medium,
  color: vars.bright.primary.light,
});

// 블록 ul 의 항목 간격 (list role 보존을 위해 flex gap 대신 인접 형제 마진)
globalStyle(`${sectionList} > li + li`, {
  marginTop: '4px',
});

// 스크롤바 숨김 — pseudo-element 라 globalStyle (NavTabs.css.ts 컨벤션)
globalStyle(`${menu}::-webkit-scrollbar`, {
  display: 'none',
});
